import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, 
  Search, 
  Filter, 
  Eye,
  Edit,
  Trash2,
  Plus,
  X,
  Calendar,
  Briefcase,
  CheckCircle,
  Clock,
  XCircle,
  Users,
  LayoutGrid,
  Shield,
  Layers,
  Download,
  Upload,
  FileSpreadsheet,
  RefreshCcw,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  FileOutput,
  DollarSign,
  User,
  List,
  LayoutList,
  Check,
  Ban
} from 'lucide-react';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import FacturationListPageLayout, {
  facturationTableScrollClass,
} from '../facturation/components/FacturationListPageLayout';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import {
  buildHrColumnVisibilityState,
  buildHrQawaemColumnDefs,
  buildHrImportExportProps,
  buildHrControlsStrip,
  isHrColumnVisible,
  createHrHandleToggleColumn,
  hrTableColSpan,
  useHrModalBodyLock,
  HrFormModalTabBar,
  useHrTableRowSelection,
  HrBulkDeleteFilterAction,
  formatHrEmployeeLabel,
  resolveHrEmployeeFromLabel,
  buildHrEmployeeSelectOptions,
  buildHrEmployeePickerPatch,
  resolveHrEmployeeMatricule,
  resolveHrDepartmentLabel,
  sliceHrListItems,
  useHrEmployees,
  useHrDepartments,
} from './hrListPageUtils';
import { useHrConfig } from './useHrConfig';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import localApiAdapter from '../../core/services/localApiAdapter';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import fr from 'date-fns/locale/fr';
import ar from 'date-fns/locale/ar-SA';
import 'react-datepicker/dist/react-datepicker.css';
import { useHR } from '../../core/contexts/HRContext';
import { formatBrandMoney, getBrandLocale, getBrandDatePickerLocale } from '../../shared/utils/brandCurrency';
import {
  SAUDI_CONTRACT_TYPES,
  normalizeContractType,
  contractTypeRequiresEndDate,
  calculateProbationEndDate,
  getNoticePeriodDays,
  buildSaudiSalaryPackage,
  getDefaultSaudiContractFields,
  SAUDI_PROBATION_DAYS_DEFAULT,
} from './utils/hrSaudiFormat';
import { calculateEOSBProvision } from '../salaires/utils/payrollSaudiFormat';
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';
import { generateReportExcel } from '../../shared/utils/reportExcelGeneratorAdvanced';
import { CustomSelect, CustomDatePicker, CustomNumberInput } from '../../shared/components/custom';
import { exportToCSV, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';
registerLocale('fr', fr);
registerLocale('ar', ar);

const HR_TABLE_COLUMNS = [
  { key: 'employee', labelKey: 'col_employee' },
  { key: 'type', labelKey: 'col_type' },
  { key: 'position', labelKey: 'col_position' },
  { key: 'department', labelKey: 'col_department' },
  { key: 'dates', labelKey: 'col_dates' },
  { key: 'salary', labelKey: 'col_salary' },
  { key: 'status', labelKey: 'col_status' },
  { key: 'actions', labelKey: 'col_actions', required: true },
];

const EmployeeContractsPage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.ressources_humaines.pages.employeeContractsPage.${key}`, opts);
  const qawaem = isSaudiBrand();
  const { isDashboardLightMode } = useGlobalTheme();
  const dateLocale = getBrandLocale(i18n.language);
  const datePickerLocale = getBrandDatePickerLocale();
  const hrConfig = useHrConfig();

  const fmtDate = (val) => (val ? new Date(val).toLocaleDateString(dateLocale) : p('na'));

  // Use HR Context for real data
  const { 
    employeeContracts = [], 
    addEmployeeContract, 
    updateEmployeeContract, 
    removeEmployeeContract, 
    refreshData,
    loading 
  } = useHR();
  
  const contracts = employeeContracts;

  const { employees: realEmployees } = useHrEmployees();
  const { departments } = useHrDepartments();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [viewingContract, setViewingContract] = useState(null);
  useHrModalBodyLock(showModal, showViewModal);
  const [selectedContracts, setSelectedContracts] = useState([]);
  const [viewMode, setViewMode] = useState('table');
  // Main view mode state
  const [mainViewMode, setMainViewMode] = useState('all_contracts');
  const [quickFilter, setQuickFilter] = useState('all');
  
  // Advanced options toggle
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  
  // Export states
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);
  
  // Date range filter (Date objects, aligned with Employees / Formation)
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  
  // Dropdown states and refs
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const dateRangeRef = useRef(null);
  const statusFilterRef = useRef(null);
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const [formTab, setFormTab] = useState('employeeInfo');
  
  const saudiDefaults = getDefaultSaudiContractFields();
  const [formData, setFormData] = useState({
    employeeName: '',
    employeeId: '',
    contractType: SAUDI_CONTRACT_TYPES.UNLIMITED,
    position: '',
    department: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    salary: '',
    basicSalary: '',
    housingAllowance: '',
    transportAllowance: '',
    mobileAllowance: '',
    foodAllowance: '',
    totalPackage: '',
    paymentFrequency: saudiDefaults.paymentFrequency,
    noticePeriodDays: saudiDefaults.noticePeriodDays,
    status: 'actif',
    probationPeriod: SAUDI_PROBATION_DAYS_DEFAULT,
    workHours: 40,
    notes: ''
  });

  const triggerRefresh = () => refreshData();

  const getContractTypeLabel = useCallback((type) => {
    const normalized = normalizeContractType(type);
    const labels = {
      [SAUDI_CONTRACT_TYPES.UNLIMITED]: p('type_unlimited'),
      [SAUDI_CONTRACT_TYPES.LIMITED]: p('type_limited'),
      [SAUDI_CONTRACT_TYPES.PART_TIME]: p('type_part_time'),
      [SAUDI_CONTRACT_TYPES.TEMPORARY]: p('type_temporary'),
    };
    return labels[normalized] || normalized;
  }, [p]);

  // Calculate stats
  const stats = {
    total: contracts.length,
    actif: contracts.filter(c => c.status === 'actif').length,
    expire_bientot: contracts.filter(c => c.status === 'expire_bientot').length,
    expire: contracts.filter(c => c.status === 'expire').length,
    termine: contracts.filter(c => c.status === 'termine').length
  };

  // NEW: Calculate contract distribution
  const contractDistribution = useMemo(() => ({
    all_contracts: {
      title: p('dist_overview_title'),
      data: [
        { filterKey: 'actif', name: p('dist_active'), count: stats.actif, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'expire_bientot', name: p('dist_expiring_soon'), count: stats.expire_bientot, color: 'bg-gradient-to-r from-orange-500 to-orange-600' },
        { filterKey: 'expire', name: p('dist_expired'), count: stats.expire, color: 'bg-gradient-to-r from-red-500 to-red-600' },
        { filterKey: 'termine', name: p('dist_terminated'), count: stats.termine, color: 'bg-gradient-to-r from-gray-500 to-gray-600' },
      ],
    },
    par_statut: {
      title: p('dist_by_status_title'),
      data: [
        { filterKey: 'actif', name: p('dist_active'), count: stats.actif, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'expire_bientot', name: p('dist_expiring_soon'), count: stats.expire_bientot, color: 'bg-gradient-to-r from-orange-500 to-orange-600' },
        { filterKey: 'expire', name: p('dist_expired'), count: stats.expire, color: 'bg-gradient-to-r from-red-500 to-red-600' },
        { filterKey: 'termine', name: p('dist_terminated'), count: stats.termine, color: 'bg-gradient-to-r from-gray-500 to-gray-600' },
      ],
    },
    par_type: {
      title: p('dist_by_type_title'),
      data: [...new Set(contracts.map((ct) => normalizeContractType(ct.contractType)))].map((type) => ({
        filterKey: type,
        name: getContractTypeLabel(type),
        count: contracts.filter((ct) => normalizeContractType(ct.contractType) === type).length,
        color: 'bg-gradient-to-r from-blue-500 to-blue-600',
      })),
    },
    par_departement: {
      title: p('dist_by_department_title'),
      data: [...new Set(contracts.map((ct) => ct.department))].map((dept) => ({
        filterKey: dept,
        name: dept,
        count: contracts.filter((ct) => ct.department === dept).length,
        color: 'bg-gradient-to-r from-purple-500 to-purple-600',
      })),
    },
  }), [t, contracts, stats.actif, stats.expire_bientot, stats.expire, stats.termine, getContractTypeLabel]);

  const distributionInfo = contractDistribution[mainViewMode] || contractDistribution.all_contracts;

  const mainTabs = useMemo(() => ([
    { id: 'all_contracts', name: p('mainTab_general'), icon: LayoutGrid },
    { id: 'par_statut', name: p('mainTab_by_status'), icon: Shield },
    { id: 'par_type', name: p('mainTab_by_type'), icon: Briefcase },
    { id: 'par_departement', name: p('mainTab_by_department'), icon: Layers },
  ]), [t]);

  const quickFilterButtons = useMemo(() => ([
    { id: 'all', name: p('quickFilter_all'), icon: Users },
    { id: 'actif', name: p('quickFilter_active'), icon: CheckCircle },
    { id: 'expire_bientot', name: p('quickFilter_expiring_soon'), icon: AlertCircle },
    { id: 'expire', name: p('quickFilter_expired'), icon: XCircle },
    { id: 'termine', name: p('quickFilter_terminated'), icon: Clock },
  ]), [t]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) {
        setShowImportDropdown(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
      if (dateRangeRef.current && !dateRangeRef.current.contains(event.target)) {
        setIsDateRangeOpen(false);
      }
      if (statusFilterRef.current && !statusFilterRef.current.contains(event.target)) {
        setIsStatusFilterOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const departmentSelectOptions = useMemo(() => [
    { value: '', label: p('placeholder_department') },
    ...departments.map((d) => ({ value: d.id, label: d.name })),
  ], [departments, p]);

  const displaySalary = useCallback((contract) => {
    const total = contract?.totalPackage ?? contract?.salary;
    return formatBrandMoney(total ?? 0);
  }, []);

  const filteredContracts = useMemo(() => {
    const base = contracts.filter((contract) => {
      const matchesSearch = !searchTerm ||
        contract.employeeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contract.position?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contract.department?.toLowerCase().includes(searchTerm.toLowerCase());
      if (!matchesSearch) return false;
      if (quickFilter !== 'all' && contract.status !== quickFilter) return false;
      return true;
    });
    const rules = [
      textColumnRule('employee', (r) => r.employeeName || ''),
      textColumnRule('type', (r) => getContractTypeLabel(r.contractType)),
      textColumnRule('position', (r) => r.position || ''),
      textColumnRule('department', (r) => r.department || ''),
      textColumnRule('dates', (r) => (r.startDate ? new Date(r.startDate).toLocaleDateString(dateLocale) : '')),
      textColumnRule('salary', (r) => displaySalary(r)),
      exactColumnRule('status', (r) => r.status || ''),
    ];
    return sliceHrListItems(
      applyColumnFilters(base, colFilters, rules),
      hrConfig.itemsPerPage,
    );
  }, [contracts, searchTerm, quickFilter, colFilters, dateLocale, getContractTypeLabel, displaySalary, hrConfig.itemsPerPage]);

  const { allRowsSelected, someRowsSelected, handleSelectAll, toggleRecord: handleSelectRecord } =
    useHrTableRowSelection(filteredContracts, selectedContracts, setSelectedContracts);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.employeeName?.trim() || !formData.position?.trim()) {
      notifyError(showNotify, p('save_error'));
      return;
    }
    try {
      if (editingContract) {
        await updateEmployeeContract(editingContract.id, formData);
        notifyUpdated(showNotify, p('entity_label', { defaultValue: 'العقد' }));
      } else {
        await addEmployeeContract(formData);
        notifyCreated(showNotify, p('entity_label', { defaultValue: 'العقد' }));
      }
      resetForm();
    } catch (error) {
      console.error('Error saving contract:', error);
      notifyError(showNotify, p('save_error'));
    }
  };

  const handleEdit = (contract) => {
    setEditingContract(contract);
    const normalized = {
      ...contract,
      contractType: normalizeContractType(contract.contractType),
      probationPeriod: contract.probationPeriod ?? SAUDI_PROBATION_DAYS_DEFAULT,
    };
    setFormData(normalized);
    setFormTab('employeeInfo');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await removeEmployeeContract(id);
        notifyDeleted(showNotify, p('entity_label', { defaultValue: 'العقد' }));
      } catch (error) {
        console.error('Error deleting contract:', error);
        notifyError(showNotify, p('delete_error'));
      }
    }
  };

  const handleView = (contract) => {
    setViewingContract(contract);
    setShowViewModal(true);
  };

  const resetForm = () => {
    const defaults = getDefaultSaudiContractFields();
    setFormData({
      employeeName: '',
      employeeId: '',
      contractType: SAUDI_CONTRACT_TYPES.UNLIMITED,
      position: '',
      department: '',
      startDate: new Date().toISOString().split('T')[0],
      endDate: '',
      salary: '',
      basicSalary: '',
      housingAllowance: '',
      transportAllowance: '',
      mobileAllowance: '',
      foodAllowance: '',
      totalPackage: '',
      paymentFrequency: defaults.paymentFrequency,
      noticePeriodDays: defaults.noticePeriodDays,
      status: 'actif',
      probationPeriod: SAUDI_PROBATION_DAYS_DEFAULT,
      workHours: 40,
      notes: ''
    });
    setEditingContract(null);
    setFormTab('employeeInfo');
    setShowModal(false);
  };

  const openNewContractModal = () => {
    const defaults = getDefaultSaudiContractFields();
    setEditingContract(null);
    setFormData({
      employeeName: '',
      employeeId: '',
      contractType: SAUDI_CONTRACT_TYPES.UNLIMITED,
      position: '',
      department: '',
      startDate: new Date().toISOString().split('T')[0],
      endDate: '',
      salary: '',
      basicSalary: '',
      housingAllowance: '',
      transportAllowance: '',
      mobileAllowance: '',
      foodAllowance: '',
      totalPackage: '',
      paymentFrequency: defaults.paymentFrequency,
      noticePeriodDays: defaults.noticePeriodDays,
      status: 'actif',
      probationPeriod: SAUDI_PROBATION_DAYS_DEFAULT,
      workHours: 40,
      notes: ''
    });
    setFormTab('employeeInfo');
    setShowModal(true);
  };

  const syncSalaryFromBasic = (basicVal, prev) => {
    const pkg = buildSaudiSalaryPackage(basicVal);
    return {
      ...prev,
      basicSalary: basicVal,
      housingAllowance: pkg.housingAllowance,
      transportAllowance: pkg.transportAllowance,
      mobileAllowance: pkg.mobileAllowance,
      foodAllowance: pkg.foodAllowance,
      totalPackage: pkg.totalPackage,
      salary: pkg.totalPackage,
    };
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('bulk_delete_confirm', { count: selectedContracts.length }))) {
      try {
        const deleteCount = selectedContracts.length;
        await Promise.all(selectedContracts.map(id => removeEmployeeContract(id)));
        notifyBulkDeleted(showNotify, deleteCount, p('entity_label_plural', { defaultValue: 'عقد' }));
        setSelectedContracts([]);
      } catch (error) {
        console.error('Error bulk deleting:', error);
        notifyError(showNotify, p('delete_error'));
      }
    }
  };

  const handleExportPDF = async () => {
    try {
      setIsGeneratingPDF(true);
      const reportData = filteredContracts.map(c => ({
        [p('export_col_employee')]: c.employeeName,
        [p('export_col_type')]: getContractTypeLabel(c.contractType),
        [p('export_col_position')]: c.position,
        [p('export_col_department')]: c.department,
        [p('export_col_start')]: new Date(c.startDate).toLocaleDateString(dateLocale),
        [p('export_col_end')]: c.endDate ? new Date(c.endDate).toLocaleDateString(dateLocale) : p('na'),
        [p('export_col_salary')]: displaySalary(c),
        [p('export_col_status')]: getStatusLabel(c.status)
      }));

      await generateReportPDF(p('report_title'), reportData, { orientation: 'landscape' });
    } catch (error) {
      console.error('PDF export error:', error);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setIsGeneratingExcel(true);
      const reportData = filteredContracts.map(c => ({
        [p('export_col_employee')]: c.employeeName,
        [p('export_col_type')]: getContractTypeLabel(c.contractType),
        [p('export_col_position')]: c.position,
        [p('export_col_department')]: c.department,
        [p('export_col_start')]: new Date(c.startDate).toLocaleDateString(dateLocale),
        [p('export_col_end')]: c.endDate ? new Date(c.endDate).toLocaleDateString(dateLocale) : p('na'),
        [p('export_col_salary')]: displaySalary(c),
        [p('export_col_status')]: getStatusLabel(c.status)
      }));

      await generateReportExcel(p('report_title'), reportData);
    } catch (error) {
      console.error('Excel export error:', error);
    } finally {
      setIsGeneratingExcel(false);
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      actif: p('status_active'),
      expire_bientot: p('status_expiring_soon'),
      expire: p('status_expired'),
      termine: p('status_terminated'),
    };
    return labels[status] || p('na');
  };

  const contractTypeOptions = useMemo(() => ([
    { value: SAUDI_CONTRACT_TYPES.UNLIMITED, label: p('type_unlimited_full') },
    { value: SAUDI_CONTRACT_TYPES.LIMITED, label: p('type_limited_full') },
    { value: SAUDI_CONTRACT_TYPES.PART_TIME, label: p('type_part_time_full') },
    { value: SAUDI_CONTRACT_TYPES.TEMPORARY, label: p('type_temporary_full') },
  ]), [p]);

  const contractFormStatusOptions = useMemo(() => ([
    { value: 'actif', label: p('status_active') },
    { value: 'expire_bientot', label: p('status_expiring_soon') },
    { value: 'expire', label: p('status_expired') },
    { value: 'termine', label: p('status_terminated') },
  ]), [t]);

  const handleExport = () => {
    const headers = [
      { key: 'employeeName', label: p('export_col_employee') },
      { key: 'employeeId', label: p('label_employee_id') },
      { key: 'contractTypeLabel', label: p('export_col_type') },
      { key: 'position', label: p('export_col_position') },
      { key: 'department', label: p('export_col_department') },
      { key: 'startDateFr', label: p('export_col_start') },
      { key: 'endDateFr', label: p('export_col_end') },
      { key: 'salaryFormatted', label: p('export_col_salary') },
      { key: 'statusLabel', label: p('export_col_status') },
      { key: 'probationPeriod', label: p('export_col_probation') },
      { key: 'workHours', label: p('export_col_hours') },
      { key: 'notes', label: p('export_col_notes') }
    ];
    const dataToExport = filteredContracts.map((c) => ({
      ...c,
      contractTypeLabel: getContractTypeLabel(c.contractType),
      startDateFr: c.startDate ? new Date(c.startDate).toLocaleDateString(dateLocale) : '',
      endDateFr: c.endDate ? new Date(c.endDate).toLocaleDateString(dateLocale) : '',
      salaryFormatted: displaySalary(c),
      statusLabel: getStatusLabel(c.status),
      probationPeriod: c.probationPeriod ?? '',
      workHours: c.workHours ?? '',
      notes: c.notes ?? ''
    }));
    const fileName = `contrats_employes_export_${new Date().toISOString().split('T')[0]}.csv`;
    try {
      exportToCSV(dataToExport, headers, fileName);
    } catch (error) {
      console.error('CSV export error:', error);
      alert(p('export_error'));
    }
  };

  const handleImport = () => {
    const headers = [
      { key: 'employeeName', label: p('col_employee') },
      { key: 'employeeId', label: p('label_employee_id') },
      { key: 'contractType', label: p('import_type_hint') },
      { key: 'position', label: p('col_position') },
      { key: 'department', label: p('col_department') },
      { key: 'startDate', label: p('label_start_date') },
      { key: 'endDate', label: p('label_end_date') },
      { key: 'salary', label: p('col_salary') },
      { key: 'status', label: p('col_status') },
      { key: 'probationPeriod', label: p('label_probation') },
      { key: 'workHours', label: p('label_work_hours_view') },
      { key: 'notes', label: p('label_notes') }
    ];
    const validStatuses = ['actif', 'expire_bientot', 'expire', 'termine'];
    const validTypes = Object.values(SAUDI_CONTRACT_TYPES);
    const schema = {
      requiredFields: ['employeeName'],
      validators: {
        status: (value) => !value || validStatuses.includes(String(value).toLowerCase()),
        contractType: (value) => !value || validTypes.includes(String(value).toLowerCase())
      }
    };
    triggerFileInput(async (file) => {
      try {
        const result = await importFromFile(file, headers, schema);
        if (result.success && result.data?.length) {
          let successCount = 0;
          for (const row of result.data) {
            try {
              const st = row.status ? String(row.status).toLowerCase().replace(/\s/g, '_') : 'actif';
              const status = validStatuses.includes(st) ? st : 'actif';
              const ct = row.contractType ? String(row.contractType).toLowerCase() : SAUDI_CONTRACT_TYPES.UNLIMITED;
              const contractType = validTypes.includes(ct) ? ct : normalizeContractType(ct);
              await addEmployeeContract({
                employeeName: row.employeeName,
                employeeId: row.employeeId || '',
                contractType,
                position: row.position || '',
                department: row.department || '',
                startDate: row.startDate || new Date().toISOString().split('T')[0],
                endDate: row.endDate || '',
                salary: row.salary != null && row.salary !== '' ? String(row.salary) : '',
                status,
                probationPeriod: row.probationPeriod != null && row.probationPeriod !== '' ? Number(row.probationPeriod) : SAUDI_PROBATION_DAYS_DEFAULT,
                workHours: row.workHours != null && row.workHours !== '' ? Number(row.workHours) : 40,
                notes: row.notes || ''
              });
              successCount++;
            } catch (err) {
              console.error('Contract import row error:', err);
            }
          }
          await refreshData();
          alert(p('import_success', { count: successCount, ignored: result.invalidCount ? p('import_ignored', { count: result.invalidCount }) : '' }));
        } else {
          alert(result.errors?.length ? result.errors.slice(0, 8).join('\n') : p('import_no_data'));
        }
      } catch (error) {
        console.error('Import error:', error);
        alert(p('import_error'));
      }
    });
  };

  const contractStatusOptions = useMemo(() => ([
    { value: 'actif', label: p('status_active'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'expire', label: p('status_expired'), icon: XCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' },
    { value: 'termine', label: p('status_resigned'), icon: Ban, style: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
    { value: 'expire_bientot', label: p('status_in_progress'), icon: AlertCircle, style: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
  ]), [t]);

  const handleStatusChange = async (recordId, newStatus) => {
    const c = contracts.find((x) => String(x.id) === String(recordId));
    if (!c) return;
    try {
      await localApiAdapter.put(`/hr/contracts/${recordId}`, {
        employee_id: c.employeeId || null,
        employee_name: c.employeeName,
        contract_type: c.contractType,
        position: c.position,
        department: c.department || '',
        start_date: c.startDate,
        end_date: c.endDate || null,
        salary: c.salary,
        status: newStatus,
        probation_period: c.probationPeriod ?? 3,
        work_hours: c.workHours ?? 40,
        notes: c.notes ?? ''
      });      await refreshData();
      setViewingContract((prev) =>
        prev && String(prev.id) === String(recordId) ? { ...prev, status: newStatus } : prev
      );
    } catch (error) {
      console.error('Error updating contract status:', error);
    }
  };

  const qawaemStatusOptions = useMemo(
    () => [
      { value: 'all', label: p('quickFilter_all') },
      ...contractStatusOptions.map((s) => ({ value: s.value, label: s.label })),
    ],
    [contractStatusOptions, p],
  );

  const listStats = useMemo(
    () => [
      { key: 'total', label: p('stat_total'), value: stats.total, icon: FileText, variant: 'copper' },
      { key: 'active', label: p('stat_active'), value: stats.actif, icon: CheckCircle, variant: 'forest' },
      { key: 'expiring', label: p('stat_expiring_soon'), value: stats.expire_bientot, icon: AlertCircle, variant: 'copper' },
      { key: 'expired', label: p('stat_expired'), value: stats.expire, icon: XCircle, variant: 'forest' },
    ],
    [stats, p],
  );

  const [columnVisibility, setColumnVisibility] = useState(() =>
    buildHrColumnVisibilityState(HR_TABLE_COLUMNS),
  );
  const qawaemColumns = useMemo(
    () => buildHrQawaemColumnDefs(HR_TABLE_COLUMNS, p, columnVisibility),
    [p, columnVisibility],
  );
  const handleToggleColumn = createHrHandleToggleColumn(setColumnVisibility, HR_TABLE_COLUMNS);
  const qawaemVisibleColumnCount = qawaemColumns.filter((c) => c.checked !== false).length;
  const isColVisible = (key) => isHrColumnVisible(columnVisibility, key);
  const tableColSpan = hrTableColSpan(columnVisibility);

  const contractStatusFilterOptions = useMemo(
    () => contractStatusOptions.map((s) => ({ value: s.value, label: s.label })),
    [contractStatusOptions],
  );

  const importExportProps = useMemo(
    () => buildHrImportExportProps({
      showImportDropdown,
      setShowImportDropdown,
      showExportDropdown,
      setShowExportDropdown,
      importDropdownRef,
      exportDropdownRef,
      p,
      t,
      isGeneratingPDF,
      handlers: {
        onImport: handleImport,
        onExportCSV: handleExport,
        onExportExcel: handleExportExcel,
        onExportPDF: handleExportPDF,
      },
    }),
    [showImportDropdown, showExportDropdown, isGeneratingPDF, p, t],
  );

  const contractFormTabs = useMemo(() => ([
    { id: 'employeeInfo', label: p('section_employee'), icon: User, iconClassName: 'text-blue-400' },
    { id: 'contractDetails', label: p('section_contract'), icon: FileText, iconClassName: 'text-green-400' },
    { id: 'financialInfo', label: p('section_financial'), icon: DollarSign, iconClassName: 'text-amber-400' },
  ]), [p]);

  const advancedOptionsContent = (
    <div className="space-y-6 pt-0">
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20 shadow-xl shadow-black/10">
        <div className="flex space-x-1 overflow-x-auto table-scroll">
          {mainTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setMainViewMode(tab.id);
                setQuickFilter('all');
              }}
              className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                mainViewMode === tab.id
                  ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg'
                  : 'text-white/80 hover:bg-white/10 hover:text-white'
              }`}
            >
              <tab.icon className="h-5 w-5" />
              <span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
            </button>
          ))}
        </div>
      </div>
      {mainViewMode === 'all_contracts' && (
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20 shadow-xl shadow-black/10">
          <div className="flex space-x-1 overflow-x-auto table-scroll">
            {quickFilterButtons.map((filter) => (
              <button
                key={filter.id}
                onClick={() => {
                  setQuickFilter(filter.id);
                }}
                className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                  quickFilter === filter.id
                    ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg'
                    : 'text-white/80 hover:bg-white/10 hover:text-white'
                }`}
              >
                <filter.icon className="h-5 w-5" />
                <span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 shadow-xl shadow-black/10"
      >
        <h3 className="text-xl font-semibold text-white mb-4">{distributionInfo.title}</h3>
        <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '3')}>{distributionInfo.data.map((item) => (
          <div key={item.filterKey || item.name} className="bg-white/5 rounded-xl p-4 border border-white/10">
            <div className="flex justify-between items-center mb-2">
              <h4 className="font-medium text-white">{item.name}</h4>
              <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white`}>{item.count}</div>
            </div>
            <div className="w-full bg-white/10 h-3 rounded-full">
              <div className={`h-3 rounded-full ${item.color}`} style={{ width: `${(() => {
                const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
              })()}%` }} />
            </div>
            <p className="mt-2 text-sm text-white/60">
              {p('percent_of_contracts', { percent: (() => {
                const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
              })() })}
            </p>
          </div>
        ))}
        </div>
      </motion.div>
    </div>
  );


  return (
    <>
      <FacturationListPageLayout
        title={p('title')}
        subtitle={p('subtitle')}
        icon={FileText}
        stats={listStats}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        filterActions={
          <HrBulkDeleteFilterAction
            count={selectedContracts.length}
            onDelete={handleBulkDelete}
            label={p('delete_count', { count: selectedContracts.length })}
          />
        }
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) })}
        columns={qawaemColumns}
        visibleColumnCount={qawaemVisibleColumnCount}
        totalColumnCount={qawaemColumns.length}
        onCreate={openNewContractModal}
        createLabel={p('new_contract')}
        importExport={importExportProps}
        headerActions={
          <button type="button" onClick={triggerRefresh} className="refresh-button" title={p('refresh')}>
            <RefreshCcw size={20} />
          </button>
        }
        showAdvancedOptions={showAdvancedOptions}
        onToggleAdvancedOptions={() => setShowAdvancedOptions(!showAdvancedOptions)}
        advancedOptionsLabel={p('advanced_options')}
        advancedOptionsContent={advancedOptionsContent}
        controlsStrip={buildHrControlsStrip({
          qawaem,
          viewMode,
          onViewModeChange: setViewMode,
          isDashboardLightMode,
          searchTerm,
          onSearchChange: setSearchTerm,
          searchPlaceholder: p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) }),
          cardsTitle: t('common.view_cards', { defaultValue: 'بطاقات' }),
          tableTitle: t('common.view_table', { defaultValue: 'جدول' }),
        })}
      >
        {viewMode === 'table' && (
          <div className={facturationTableScrollClass(qawaem)}>
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}><div className="flex justify-center"><QawaemTableCheckbox size="lg" checked={allRowsSelected} indeterminate={someRowsSelected} onChange={handleSelectAll} /></div></th>
                  {isColVisible('employee') && (
                    <TableColumnHeaderFilter columnKey="employee" label={p('col_employee')} value={colFilters.employee ?? ''} onChange={(v) => setColFilter('employee', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" />
                  )}
                  {isColVisible('type') && (
                    <TableColumnHeaderFilter columnKey="type" label={p('col_type')} value={colFilters.type ?? ''} onChange={(v) => setColFilter('type', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" />
                  )}
                  {isColVisible('position') && (
                    <TableColumnHeaderFilter columnKey="position" label={p('col_position')} value={colFilters.position ?? ''} onChange={(v) => setColFilter('position', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" />
                  )}
                  {isColVisible('department') && (
                    <TableColumnHeaderFilter columnKey="department" label={p('col_department')} value={colFilters.department ?? ''} onChange={(v) => setColFilter('department', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" />
                  )}
                  {isColVisible('dates') && (
                    <TableColumnHeaderFilter columnKey="dates" label={p('col_dates')} value={colFilters.dates ?? ''} onChange={(v) => setColFilter('dates', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" />
                  )}
                  {isColVisible('salary') && (
                    <TableColumnHeaderFilter columnKey="salary" label={p('col_salary')} value={colFilters.salary ?? ''} onChange={(v) => setColFilter('salary', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" />
                  )}
                  {isColVisible('status') && (
                    <TableColumnHeaderFilter columnKey="status" label={p('col_status')} kind="select" options={contractStatusFilterOptions} value={colFilters.status ?? ''} onChange={(v) => setColFilter('status', v)} align="center" editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" />
                  )}
                  <QawaemTableHeaderActionsCell label={p('col_actions')} columns={qawaemColumns} onToggle={handleToggleColumn} visibleCount={qawaemVisibleColumnCount} totalCount={qawaemColumns.length} thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle" />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredContracts.length > 0 ? filteredContracts.map((contract) => (
                  <tr key={contract.id} className={`hover:bg-white/5 ${selectedContracts.includes(contract.id) ? 'bg-white/10' : ''}`}>
                    <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}><div className="flex justify-center"><QawaemTableCheckbox checked={selectedContracts.includes(contract.id)} onChange={() => handleSelectRecord(contract.id)} /></div></td>
                    {isColVisible('employee') && <td className="px-6 py-4 text-white font-semibold">{contract.employeeName}</td>}
                    {isColVisible('type') && <td className="px-6 py-4 text-white/80">{getContractTypeLabel(contract.contractType)}</td>}
                    {isColVisible('position') && <td className="px-6 py-4 text-white/80">{contract.position}</td>}
                    {isColVisible('department') && <td className="px-6 py-4 text-white/80">{contract.department}</td>}
                    {isColVisible('dates') && <td className="px-6 py-4 text-white/80">{contract.startDate ? new Date(contract.startDate).toLocaleDateString(dateLocale) : '—'}</td>}
                    {isColVisible('salary') && <td className="px-6 py-4 text-white/80">{displaySalary(contract)}</td>}
                    {isColVisible('status') && <td className="px-6 py-4 text-center align-middle"><div className="flex justify-center"><QawaemClickableStatusBadge status={contract.status} recordId={contract.id} options={contractStatusOptions} onChange={handleStatusChange} /></div></td>}
                    <td className="px-6 py-4 text-center align-middle"><QawaemErpRowActions onView={() => handleView(contract)} onEdit={() => handleEdit(contract)} onDelete={() => handleDelete(contract.id)} /></td>
                  </tr>
                )) : (
                  <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60"><FileText size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty')}</p></td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {filteredContracts.length > 0 ? (
              filteredContracts.map((contract, index) => (
                <motion.div
                  key={contract.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-3 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <FileText size={20} className="text-blue-400 shrink-0" />
                          <span className="truncate">{contract.employeeName || '—'}</span>
                        </h3>
                        <QawaemClickableStatusBadge status={contract.status} recordId={contract.id} options={contractStatusOptions} onChange={handleStatusChange} title={p('click_change_status')} />
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-white/20 text-white/80">
                          {getContractTypeLabel(contract.contractType)}
                        </span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_employee_position')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div className="flex items-center gap-2">
                              <User size={16} className="text-blue-400 shrink-0" />
                              <span className="font-medium">{p('label_id')}</span>{' '}
                              <span className="text-white">{contract.employeeId || '—'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Briefcase size={16} className="text-violet-400 shrink-0" />
                              <span className="text-white">{contract.position || '—'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Users size={16} className="text-white/50 shrink-0" />
                              <span>{contract.department || '—'}</span>
                            </div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_period')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div className="flex items-center gap-2">
                              <Calendar size={16} className="text-amber-400 shrink-0" />
                              <span>
                                {contract.startDate
                                  ? new Date(contract.startDate).toLocaleDateString(dateLocale)
                                  : '—'}
                                {contract.endDate
                                  ? ` → ${new Date(contract.endDate).toLocaleDateString(dateLocale)}`
                                  : ''}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Clock size={16} className="text-white/50 shrink-0" />
                              <span>{contract.probationPeriod != null ? p('probation_days', { count: contract.probationPeriod }) : p('dash')}</span>
                            </div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_remuneration')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div className="flex items-center gap-2">
                              <DollarSign size={16} className="text-emerald-400 shrink-0" />
                              <span className="text-white">{formatBrandMoney(contract.salary)}</span>
                            </div>
                            {contract.workHours != null && (
                              <div className="text-white/60">{p('hours_per_week', { count: contract.workHours })}</div>
                            )}
                          </div>
                        </div>
                        <div className="md:col-span-2 lg:col-span-1">
                          <h5 className="font-medium text-white mb-2">{p('label_notes')}</h5>
                          {contract.notes ? (
                            <p className="text-sm text-white/60 line-clamp-3">{contract.notes}</p>
                          ) : (
                            <p className="text-sm text-white/40">{p('no_notes')}</p>
                          )}
                        </div>
                      </div>
                    </div>
                    <QawaemErpRowActions
                      compact
                      onView={() => handleView(contract)}
                      onEdit={() => handleEdit(contract)}
                      onDelete={() => handleDelete(contract.id)}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <FileText size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty')}</p>
              </div>
            )}
          </div>
        )}
      </FacturationListPageLayout>

      {/* Add/Edit Modal */}
      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" style={{ top: 0, left: 0, right: 0, bottom: 0 }}>
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }} 
            animate={{ opacity: 1, scale: 1 }} 
            className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl max-h-[90vh] flex flex-col overflow-hidden" 
            style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }}
          >
            {/* Fixed Header */}
            <div className={`flex items-center justify-between p-6 border-b ${isDashboardLightMode ? 'border-gray-200' : 'border-white/10'}`}>
              <h2 className={`text-2xl font-bold ${isDashboardLightMode ? 'text-gray-900' : 'text-white'}`}>
                {editingContract ? p('modal_edit') : p('modal_new')}
              </h2>
              <div className="flex items-center gap-3">
                <button 
                  type="submit" 
                  form="contract-form" 
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"
                >
                  <CheckCircle size={18} />
                  {editingContract ? p('update') : p('create')}
                </button>
                <button onClick={resetForm} className={isDashboardLightMode ? 'text-gray-500 hover:text-gray-800 transition-colors' : 'text-white/70 hover:text-white transition-colors'}>
                  <X size={24} />
                </button>
              </div>
            </div>
            
            
            <HrFormModalTabBar
              tabs={contractFormTabs}
              activeTab={formTab}
              onTabChange={setFormTab}
              isLight={isDashboardLightMode}
              ariaLabel={editingContract ? p('modal_edit') : p('modal_new')}
            />

            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="contract-form" onSubmit={handleSubmit} >
                {formTab === 'employeeInfo' && (
                  <>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{<RequiredLabelText text={p('label_employee_name_required')} />}</label>
                            <CustomSelect
                              value={formData.employeeName}
                              onChange={(value) => {
                                const selectedEmployee = resolveHrEmployeeFromLabel(realEmployees, value);
                                if (selectedEmployee) {
                                  const deptLabel = resolveHrDepartmentLabel(departments, selectedEmployee.affectation);
                                  const basic = selectedEmployee.salaireBase ?? selectedEmployee.salary ?? '';
                                  setFormData({
                                    ...formData,
                                    ...buildHrEmployeePickerPatch(selectedEmployee, 'employeeName', 'employeeId'),
                                    employeeMat: selectedEmployee.mat || '',
                                    department: deptLabel || formData.department,
                                    position: selectedEmployee.posteOccupe || selectedEmployee.fonction || formData.position,
                                    ...(basic ? syncSalaryFromBasic(String(basic), formData) : {}),
                                  });
                                } else {
                                  setFormData({ ...formData, employeeName: value, employeeId: '', employeeMat: '' });
                                }
                              }}
                              options={buildHrEmployeeSelectOptions(realEmployees)}
                              placeholder={p('select_employee')}
                              searchable={true}
                              creatable={true}
                            />
                            <p className="text-white/50 text-xs mt-1">{p('select_hint')}</p>
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_employee_id')}</label>
                            <input
                              type="text"
                              readOnly
                              value={formData.employeeMat || resolveHrEmployeeMatricule(realEmployees, { employeeId: formData.employeeId, employeeName: formData.employeeName }) || formData.employeeId || ''}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                              placeholder={p('placeholder_employee_id')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{<RequiredLabelText text={p('label_position_required')} />}</label>
                            <input
                              type="text"
                              required
                              value={formData.position}
                              onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                              placeholder={p('placeholder_position')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_department')}</label>
                            <CustomSelect
                              value={departments.find((d) => d.name === formData.department || d.id === formData.department)?.id || formData.department || ''}
                              onChange={(value) => {
                                const dept = departments.find((d) => d.id === value);
                                setFormData({ ...formData, department: dept?.name || value });
                              }}
                              options={departmentSelectOptions}
                              placeholder={p('placeholder_department')}
                              searchable
                              creatable
                            />
                          </div>
                    </div>
                  </>
                )}
                {formTab === 'contractDetails' && (
                  <>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{<RequiredLabelText text={p('label_contract_type_required')} />}</label>
                            <CustomSelect
                              value={formData.contractType}
                              onChange={(value) => setFormData({ ...formData, contractType: value })}
                              options={contractTypeOptions}
                              placeholder={p('select_contract_type')}
                              searchable={true}
                              creatable={true}
                            />
                            <p className="text-white/50 text-xs mt-1">{p('select_type_hint')}</p>
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_start_date')} <span className="qawaem-required-mark">*</span></label>
                            <CustomDatePicker
                              value={formData.startDate}
                              onChange={(value) => setFormData({ ...formData, startDate: value })}
                              placeholder={p('date_placeholder')}
                            />
                          </div>
                          {contractTypeRequiresEndDate(formData.contractType) && (
                            <div>
                              <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_end_date')}</label>
                              <CustomDatePicker
                                value={formData.endDate}
                                onChange={(value) => setFormData({ ...formData, endDate: value })}
                                placeholder={p('date_placeholder')}
                              />
                            </div>
                          )}
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_probation_days')}</label>
                            <CustomNumberInput
                              value={formData.probationPeriod}
                              onChange={(value) => setFormData({ ...formData, probationPeriod: value })}
                              placeholder={String(SAUDI_PROBATION_DAYS_DEFAULT)}
                              min={0}
                              max={180}
                            />
                            <p className="text-white/50 text-xs mt-1">{p('hint_probation_saudi')}</p>
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_payment_frequency')}</label>
                            <CustomSelect
                              value={formData.paymentFrequency || 'monthly'}
                              onChange={(value) => setFormData({
                                ...formData,
                                paymentFrequency: value,
                                noticePeriodDays: getNoticePeriodDays(value),
                              })}
                              options={[
                                { value: 'monthly', label: p('payment_monthly') },
                                { value: 'other', label: p('payment_other') },
                              ]}
                              placeholder={p('select')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_notice_period')}</label>
                            <CustomNumberInput
                              value={formData.noticePeriodDays ?? getNoticePeriodDays(formData.paymentFrequency)}
                              onChange={(value) => setFormData({ ...formData, noticePeriodDays: value })}
                              placeholder="30"
                              min={0}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_work_hours')}</label>
                            <CustomNumberInput
                              value={formData.workHours}
                              onChange={(value) => setFormData({ ...formData, workHours: value })}
                              placeholder="40"
                              min={0}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{<RequiredLabelText text={p('label_status_required')} />}</label>
                            <CustomSelect
                              value={formData.status}
                              onChange={(value) => setFormData({ ...formData, status: value })}
                              options={contractFormStatusOptions}
                              placeholder={p('select_status')}
                            />
                          </div>
                    </div>
                  </>
                )}
                {formTab === 'financialInfo' && (
                  <>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                              <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_basic_salary')}</label>
                              <CustomNumberInput
                                value={formData.basicSalary ?? formData.salary}
                                onChange={(value) => setFormData(syncSalaryFromBasic(value, formData))}
                                placeholder="8000"
                                min={0}
                              />
                            </div>
                            <div>
                              <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_housing_allowance')}</label>
                              <CustomNumberInput
                                value={formData.housingAllowance}
                                onChange={(value) => setFormData({ ...formData, housingAllowance: value })}
                                placeholder="2000"
                                min={0}
                              />
                            </div>
                            <div>
                              <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_transport_allowance')}</label>
                              <CustomNumberInput
                                value={formData.transportAllowance}
                                onChange={(value) => setFormData({ ...formData, transportAllowance: value })}
                                placeholder="1500"
                                min={0}
                              />
                            </div>
                            <div>
                              <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_mobile_allowance')}</label>
                              <CustomNumberInput
                                value={formData.mobileAllowance}
                                onChange={(value) => setFormData({ ...formData, mobileAllowance: value })}
                                placeholder="500"
                                min={0}
                              />
                            </div>
                            <div>
                              <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_food_allowance')}</label>
                              <CustomNumberInput
                                value={formData.foodAllowance}
                                onChange={(value) => setFormData({ ...formData, foodAllowance: value })}
                                placeholder="800"
                                min={0}
                              />
                            </div>
                            <div>
                              <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_total_package')}</label>
                              <p className="text-white font-semibold text-lg py-2">
                                {formatBrandMoney(
                                  (parseFloat(formData.basicSalary ?? formData.salary) || 0) +
                                  (parseFloat(formData.housingAllowance) || 0) +
                                  (parseFloat(formData.transportAllowance) || 0) +
                                  (parseFloat(formData.mobileAllowance) || 0) +
                                  (parseFloat(formData.foodAllowance) || 0)
                                )}
                              </p>
                              {formData.basicSalary && (
                                <p className="text-white/50 text-xs mt-1">
                                  {p('label_eosb_accrual')}: {formatBrandMoney(calculateEOSBProvision(formData.basicSalary))}
                                </p>
                              )}
                            </div>
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_notes')}</label>
                            <textarea
                              value={formData.notes}
                              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none"
                              rows={3}
                              placeholder={p('placeholder_notes')}
                            />
                    </div>
                  </>
                )}
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {/* View Details Modal */}
      {showViewModal && viewingContract && ReactDOM.createPortal(
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4"
            onClick={() => {
              setShowViewModal(false);
              setViewingContract(null);
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
              style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth: '1100px' }}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">{p('view_modal_title')}</h2>
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    setViewingContract(null);
                  }}
                  className="text-white/70 hover:text-white transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              <div >
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <FileText size={18} className="text-blue-400" />
                    {p('view_section_contract_info')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('col_employee')}</p>
                      <p className="text-white font-medium text-lg">{viewingContract.employeeName || p('na')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_contract_type')}</p>
                      <p className="text-white font-medium">{getContractTypeLabel(viewingContract.contractType)}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('col_status')}</p>
                      <div className="flex">
                        <QawaemClickableStatusBadge status={viewingContract.status} recordId={viewingContract.id} options={contractStatusOptions} onChange={handleStatusChange} title={p('click_change_status')} />
                      </div>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('col_position')}</p>
                      <p className="text-white font-medium">{viewingContract.position || p('na')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('col_department')}</p>
                      <p className="text-white font-medium">{viewingContract.department || p('na')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_start_date')}</p>
                      <p className="text-white font-medium">
                        {viewingContract.startDate
                          ? new Date(viewingContract.startDate).toLocaleDateString(dateLocale)
                          : p('na')}
                      </p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_end_date')}</p>
                      <p className="text-white font-medium">
                        {viewingContract.endDate
                          ? new Date(viewingContract.endDate).toLocaleDateString(dateLocale)
                          : p('na')}
                      </p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_work_hours_view')}</p>
                      <p className="text-white font-medium">
                        {viewingContract.workHours != null ? p('hours_per_week_short', { count: viewingContract.workHours }) : p('na')}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <Briefcase size={18} className="text-green-400" />
                      {p('view_section_salary_probation')}
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_salary')}</p>
                        <p className="text-white font-medium text-lg">{displaySalary(viewingContract)}</p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_probation')}</p>
                        <p className="text-white font-medium">
                          {viewingContract.probationPeriod != null ? p('probation_days_short', { count: viewingContract.probationPeriod }) : p('na')}
                        </p>
                        {viewingContract.startDate && (
                          <p className="text-white/50 text-xs mt-1">
                            {p('label_probation_end')}: {calculateProbationEndDate(viewingContract.startDate, viewingContract.probationPeriod)
                              ? new Date(calculateProbationEndDate(viewingContract.startDate, viewingContract.probationPeriod)).toLocaleDateString(dateLocale)
                              : p('na')}
                          </p>
                        )}
                      </div>
                      {viewingContract.basicSalary && (
                        <div>
                          <p className="text-white/60 text-sm mb-1">{p('label_eosb_accrual')}</p>
                          <p className="text-white font-medium">{formatBrandMoney(calculateEOSBProvision(viewingContract.basicSalary))}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <Layers size={18} className="text-violet-400" />
                      {p('label_notes')}
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_comments')}</p>
                        <p className="text-white font-medium">{viewingContract.notes || p('no_notes')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    setViewingContract(null);
                    handleEdit(viewingContract);
                  }}
                  className="px-6 py-2 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-colors"
                >
                  {p('action_edit')}
                </button>
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    setViewingContract(null);
                  }}
                  className="px-6 py-2 rounded-xl bg-white/10 text-white hover:bg-white/20 transition-colors"
                >
                  {p('close')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </>
  );
};

export default EmployeeContractsPage;

